import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from '@structural-climbing/ui'

interface AddSectionModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
  apiUrl: string
  /** Section discipline to create with, fixed by the active Routes tab. */
  discipline?: string
}

const DISCIPLINE_KEYS: Record<string, string> = {
  bouldering: 'routes.disciplineBouldering',
  rope: 'routes.disciplineRope',
  other: 'routes.disciplineOther',
}

export function AddSectionModal({
  isOpen,
  onClose,
  onCreated,
  apiUrl,
  discipline = 'bouldering',
}: AddSectionModalProps) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()

  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function handleClose() {
    setName('')
    setError(null)
    onClose()
  }

  async function handleSubmit() {
    const trimmed = name.trim()
    if (!trimmed) {
      setError(t('routes.sectionNameRequired'))
      return
    }

    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/gym-sections`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed, discipline }),
      })
      if (!res.ok) {
        const json = (await res.json()) as { error?: string }
        throw new Error(json.error ?? t('routes.errorCreateSection'))
      }
      onCreated()
      handleClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('routes.errorCreateSection'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      scrollBehavior="inside"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>{t('routes.addSectionTitle')}</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <Input
                  label={t('routes.sectionName')}
                  value={name}
                  onValueChange={setName}
                  isRequired
                />
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-foreground/60">
                    {t('routes.sectionDiscipline')}
                  </span>
                  <span className="w-fit rounded-full bg-default-100 px-2 py-0.5 text-sm text-foreground/70">
                    {t(DISCIPLINE_KEYS[discipline] ?? 'routes.disciplineOther')}
                  </span>
                </div>
                {error && <p className="text-sm text-danger">{error}</p>}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onClick={handleClose} isDisabled={loading}>
                {t('common.cancel')}
              </Button>
              <Button color="primary" onClick={handleSubmit} isLoading={loading}>
                {t('routes.addSection')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
